import { forwardRef, type InputHTMLAttributes, type ReactElement, type ReactNode } from "react";
import { classNames } from "../../classNames";
import { Icon, type IconName } from "../../icons";
import { useFieldControl } from "../Field/FieldContext";
import { InlineLabel } from "./InlineLabel";

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** An icon of the set, or a custom 18 px glyph (e.g. a brand mark). */
  icon?: IconName | ReactElement;
  /** Short unit shown inside the right edge (e.g. "minutos"). */
  suffix?: string;
  /** Interactive element inside the right edge (e.g. a show/hide button). */
  trailing?: ReactNode;
  size?: "sm" | "md";
  mono?: boolean;
  invalid?: boolean;
  /** Name shown inside the box, above the value, and kept while typing (for inputs without a Field label). */
  inlineLabel?: string;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { icon, suffix, trailing, size = "md", mono, invalid, inlineLabel, className, id, ...rest },
  ref,
) {
  const control = useFieldControl({ id, invalid, inlineLabel, describedBy: rest["aria-describedby"] });
  const wrapClasses = classNames("nb-input-wrap", trailing ? "has-trail" : null, suffix ? "has-suffix" : null, inlineLabel ? "has-label" : null);
  const inputClasses = classNames("nb-input", size === "sm" ? "nb-input-sm" : null, mono ? "nb-input-mono" : null, className);
  return (
    <div className={wrapClasses}>
      <InputIcon icon={icon} />
      <InlineLabel text={inlineLabel} htmlFor={control.id} />
      <input ref={ref} className={inputClasses} {...control} {...rest} />
      {suffix ? <span className="nb-input-suffix">{suffix}</span> : null}
      {trailing ? <span className="nb-input-trail">{trailing}</span> : null}
    </div>
  );
});

/** A named icon of the set, or a custom glyph wrapped like one. */
function InputIcon({ icon }: { icon: IconName | ReactElement | undefined }): React.JSX.Element | null {
  if (typeof icon === "string") return <Icon name={icon} className="nb-input-icon" />;
  if (!icon) return null;
  return (
    <span className="nb-input-icon" aria-hidden="true">
      {icon}
    </span>
  );
}
